import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const RegisterStep3 = () => {
    const {
        formData,
        handleInputChange,
        errors,
        loading
    } = useOutletContext();

    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const password = formData.password || '';
    const confirmPassword = formData.confirmPassword || '';
    const passwordRules = {
        length: password.length >= 6,
        match: password.length > 0 && password === confirmPassword
    };
    const isAllRulesMet = passwordRules.length && passwordRules.match;

    // Safety check: redirect to earlier steps if details are missing
    useEffect(() => {
        if (!formData.fullName || !formData.dateOfBirth || !formData.gender) {
            navigate('/register/step-1', { replace: true });
        } else if (!formData.phone || !formData.district || !formData.city) {
            navigate('/register/step-2', { replace: true });
        }
    }, [formData.fullName, formData.dateOfBirth, formData.gender, formData.phone, formData.district, formData.city, navigate]);

    return (
        <>
            {/* Password */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.password ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                    </Box>
                    <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <Box
                        component="input"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        sx={{
                            flex: 1,
                            padding: '16px',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '15px',
                            color: '#334155',
                            outline: 'none',
                            fontFamily: "'Poppins', sans-serif",
                            width: '100%'
                        }}
                    />
                    <Box
                        component="button"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        sx={{
                            px: 2,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#94a3b8',
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: '#64748b'
                            }
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {showPassword ? (
                                <>
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </>
                            ) : (
                                <>
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </>
                            )}
                        </svg>
                    </Box>
                </Box>
                {errors.password && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.password}
                    </Typography>
                )}
            </Box>

            {/* Confirm Password */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.confirmPassword ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                    </Box>
                    <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <Box
                        component="input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        sx={{
                            flex: 1,
                            padding: '16px',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '15px',
                            color: '#334155',
                            outline: 'none',
                            fontFamily: "'Poppins', sans-serif",
                            width: '100%'
                        }}
                    />
                    <Box
                        component="button"
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        sx={{
                            px: 2,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#94a3b8',
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: '#64748b'
                            }
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {showConfirmPassword ? (
                                <>
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </>
                            ) : (
                                <>
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </>
                            )}
                        </svg>
                    </Box>
                </Box>
                {errors.confirmPassword && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.confirmPassword}
                    </Typography>
                )}
            </Box>

            {/* Password strength checklist */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mt: 1.5,
                    mb: 1
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {passwordRules.length ? (
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="10" fill="#46CC6B" />
                            <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="9" stroke="#cbd5e1" strokeWidth="2" fill="none" />
                        </svg>
                    )}
                    <Typography sx={{ fontSize: '13.5px', color: passwordRules.length ? '#475569' : '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>
                        6 characters minimum
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {passwordRules.match ? (
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="10" fill="#46CC6B" />
                            <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="9" stroke="#cbd5e1" strokeWidth="2" fill="none" />
                        </svg>
                    )}
                    <Typography sx={{ fontSize: '13.5px', color: passwordRules.match ? '#475569' : '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>
                        Password and confirm password must match
                    </Typography>
                </Box>
            </Box>

            {/* Agree to Terms & Conditions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif",
                        userSelect: 'none',
                        mt: 1
                    }}
                >
                    <Box
                        component="input"
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        sx={{
                            appearance: 'none',
                            width: '20px',
                            height: '20px',
                            border: '1.5px solid #ffaa95',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'inline-grid',
                            placeContent: 'center',
                            marginRight: '10px',
                            transition: 'all 0.2s ease',
                            '&:checked': {
                                backgroundColor: '#ff502d',
                                borderColor: '#ff502d',
                                '&::before': {
                                    content: '""',
                                    width: '10px',
                                    height: '6px',
                                    borderLeft: '2px solid white',
                                    borderBottom: '2px solid white',
                                    transform: 'rotate(-45deg) translate(1px, -1px)'
                                }
                            }
                        }}
                    />
                    <span>
                        I agree to the{' '}
                        <Box
                            component="a"
                            href="https://eduone.lk/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#ff502d',
                                textDecoration: 'underline',
                                fontWeight: 500,
                                '&:hover': { color: '#e04324' }
                            }}
                        >
                            Terms & Conditions
                        </Box>
                        {' and '}
                        <Box
                            component="a"
                            href="https://eduone.lk/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#ff502d',
                                textDecoration: 'underline',
                                fontWeight: 500,
                                '&:hover': { color: '#e04324' }
                            }}
                        >
                            Privacy Policy
                        </Box>
                    </span>
                </Box>
                {errors.agreeToTerms && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.agreeToTerms}
                    </Typography>
                )}
            </Box>

            {/* Navigation Buttons for Step 3 */}
            <Box sx={{ display: 'flex', gap: 2, mt: '10px' }}>
                <Button
                    type="button"
                    onClick={() => navigate('/register/step-2')}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderColor: '#ff502d',
                        color: '#ff502d',
                        borderRadius: '10px',
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontFamily: "'Poppins', sans-serif",
                        '&:hover': {
                            borderColor: '#e04324',
                            color: '#e04324',
                            background: 'rgba(255, 80, 45, 0.05)'
                        }
                    }}
                >
                    Back
                </Button>
                <Button
                    type="submit"
                    disabled={
                        loading ||
                        !isAllRulesMet ||
                        !(formData.confirmPassword || '').trim() ||
                        !formData.agreeToTerms
                    }
                    variant="contained"
                    sx={{
                        flex: 1,
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 3px 8px rgba(255, 80, 45, 0.15)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-1.5px)',
                            boxShadow: '0 5px 12px rgba(255, 80, 45, 0.25)'
                        },
                        '&:disabled': {
                            opacity: 0.6,
                            backgroundColor: '#ff502d',
                            color: '#ffffff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Register'}
                </Button>
            </Box>
        </>
    );
};

export default RegisterStep3;
